//@ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Paperclip, X } from "lucide-react";
import CustomMenuDropdown from "../../common/CustomMenuDropdown";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { CrossIcon } from "../../icons/Icons";
import {
  createAppScoreCardDataThunk,
  fetchScorecardByListDataFilter,
} from "../../../features/ApplicationData/ApplicationDataSlice";
import {
  deleteAppScorecardDetails,
  fetchScoreCardPipelineDetailsData,
} from "../../../features/scorecard/scoreCardSlice";
import { motion, AnimatePresence } from "framer-motion";
import { fetchAppSkillRatingByDetails } from "../../../features/AppSkillRating/AppSkillRatingSlice";
import { fetchApplicationQuestionnaireByListDataFilter } from "../../../features/applicationQuestionnaire/applicationQuestionnaireSlice";
import {
  fetchJobPipelineDetailsData,
  fetchEvaluationData,
} from "../../../features/pipeline/pipelineSlice";
import CreateEvaluation from "./CreateEvaluation";
import { fetchQuestionEvaluationStepList } from "../../../features/jobEvaluation/JobApplicationSlice";
import { ExclamationIcon } from "../../icons";
import { format } from "date-fns";
import { getColorForLabel } from "../../../utils/colorUtils";

// AI Icon Component
const AiIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    aria-hidden="true"
    data-slot="icon"
    className=" mr-[6px] w-[1rem] h-[1rem] text-[#808080] "
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
    />
  </svg>
);
export interface FormElement {
  id: string;
  type: string;
  label: string;
  icon: string;
  headerText?: string;
  heading?: string;
  placeholder?: string;
  options?: DropdownOption[];
  value?: string;
  required?: boolean;
  step_sequence?: number;
}

// Function to determine badge color based on skill scale value
const getSkillScaleBadgeStyles = (scale: any) => {
  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10",
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10",
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10",
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10",
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10",
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10",
    };
  }
};

// Function to get skill level label based on scale value
const getSkillLevelLabel = (scale: any) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Expert";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Proficient";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Competent";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "Limited";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Inadequate";

  return null;
};

// Function to determine rating color
const getRatingColor = (rating: any) => {
  if (!rating) return "#9ca3af"; // Gray for null ratings
  const score = parseFloat(rating);
  if (score >= 4) return "#10b981"; // Green for high ratings
  if (score >= 2.5) return "#f59e0b"; // Yellow/orange for medium
  return "#ef4444"; // Red for low ratings
};

const getRatingLabel = (rating: any) => {
  if (!rating) return null;

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) return "Strong Yes";
  if (ratingValue === 4) return "Yes";
  if (ratingValue === 3) return "Neutral";
  if (ratingValue === 2) return "No";
  if (ratingValue === 1) return "Strong No";

  return null;
};

// Function to get badge styles based on rating value
const getRatingBadgeStyles = (rating: any) => {
  if (!rating)
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10",
    };

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) {
    // Strong Yes: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10",
    };
  } else if (ratingValue === 4) {
    // Yes: Light Green
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-700/10",
    };
  } else if (ratingValue === 3) {
    // Neutral: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10",
    };
  } else if (ratingValue === 2) {
    // No: Orange
    return {
      bg: "bg-orange-50",
      text: "text-orange-700",
      ring: "ring-orange-700/10",
    };
  } else if (ratingValue === 1) {
    // Strong No: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10",
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10",
    };
  }
};

// Individual circular progress component
const CircularRatingProgress = ({
  rating,
  maxRating = 5,
  size = 40,
  strokeWidth = 5,
  color = "#3b82f6",
}) => {
  // Handle null or undefined ratings
  const actualRating = rating ? parseFloat(rating) : 0;

  // Calculate percentage
  const percentage = (actualRating / maxRating) * 100;

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Center position
  const center = size / 2;

  return (
    <div className="flex items-center justify-center bg-white rounded-lg">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>

        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-bold" style={{ color }}>
            {rating || "-"}
          </span>
          {/* <span className="text-xs text-gray-500">Score</span> */}
        </div>
      </div>
    </div>
  );
};

const ratingOptions1 = [
  { value: 1, label: "Strong No" },
  { value: 2, label: "No" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Yes" },
  { value: 5, label: "Strong Yes" },
];

const RatingButton1 = ({ label, value, selectedValue, onClick }) => {
  const isSelected = selectedValue == value;

  return (
    <button
      className={`flex items-center gap-[6px] text-[13px] rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${
        isSelected
          ? "bg-indigo-600 text-white ring-1 ring-inset ring-indigo-600"
          : "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10"
      }`}
      onClick={() => onClick(value)}
      type="button"
    >
      {label}
    </button>
  );
};

function ApplicationEvaluationSection({
  job: jobProp,
  app: appProp,
  quickData,
  onQuestionnaireAction,
}) {
  const [scorecard, setScorecard] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    baseId,
    jobId: jobIdFromParams,
    applicationId: applicationIdFromParams,
    skillId,
  } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
    skillId: string;
  }>();

  const jobId = jobProp || jobIdFromParams;
  const applicationId = appProp || applicationIdFromParams || quickData;

  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const userDetailsData = localStorage.getItem("jobByHiringTeamMembers");
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const [pipelineStages, setPipelineStages] = useState([]);
  const [openAccordionIds, setOpenAccordionIds] = useState(() => {
    const initialState = {};
    if (scorecard && scorecard.skill) {
      scorecard.skill.forEach((item) => {
        initialState[item.id] = true;
      });
    }
    return initialState;
  });
  const navigate = useNavigate();
  const [showSkills, setShowSkills] = useState(false);
  const [openAccordionIds1, setOpenAccordionIds1] = useState({});
  const [appScorecardData, setAppScorecardData] = useState([]);
  const [loadingAppScorecards, setLoadingAppScorecards] = useState({});
  const [openSkillAccordionIds, setOpenSkillAccordionIds] = useState({});
  const [openDeleteModalExp, setOpenDeleteModalExp] = useState(false);
  const [deleteLoadingExp, setDeleteLoadingExp] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [openAccordionIds1Candidate, setOpenAccordionIds1Candidate] = useState(
    {},
  );
  const [overallRating, setOverallRating] = useState(null);
  const [overallComment, setOverallComment] = useState("");
  const [appScorecardCandidateOpen, setAppScorecardCandidateOpen] =
    useState(false);
  const [overallRatingError, setOverallRatingError] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [
    openAccordionIds1CandidateCreate,
    setOpenAccordionIds1CandidateCreate,
  ] = useState({});
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [openDeleteModalExpCandidate, setOpenDeleteModalExpCandidate] =
    useState(false);
  const [deleteLoadingExpCandidate, setDeleteLoadingExpCandidate] =
    useState(false);
  const [selectedIndexCandidate, setSelectedIndexCandidate] = useState(null);
  const [openMeeting, setOpenMeeting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [elements, setElements] = useState<FormElement[]>([]);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [pipelineStageData, setPipelineStageData] = useState([]);

  useEffect(() => {
    const storedUserDetails = JSON.parse(
      localStorage.getItem("userDetails") || "{}",
    );
    setUserId(storedUserDetails?.id); // Assuming the user ID is stored as 'id' in userDetails
  }, []);
  // all integration steps details

  // useEffect(() => {
  //   const fetchSteps = async () => {
  //     if (jobId && baseId) {
  //       setLoading(true);
  //       try {
  //         const response = await dispatch(
  //           fetchQuestionEvaluationStepList({ baseId, jobId }),
  //         ).unwrap();
  //         // setElements([response?.data]);
  //       } catch (error) {
  //         console.error("Failed to fetch integration steps:", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     }
  //   };
  //   fetchSteps();
  // }, [dispatch, baseId]);

  // Function to handle accordion toggle
  const handleAccordionOpen = (id: any) => {
    setOpenAccordionIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // const handleEditClick = (appScorecard: any) => {
  //   navigate(
  //     `/${baseId}/job/${jobId}/app/${applicationId}/scorecards/${appScorecard.id}/edit`,
  //   );
  // };

  const handleEditClick = (meeting) => {
    setSelectedMeeting(meeting);
    setEditMeeting(true);
  };

  const handleDeleteClick = (appScoreCardId: any) => {
    setOpenDeleteModalExp(true);
    setSelectedIndex(appScoreCardId);
  };

  // Function to handle cancel button in delete modal
  const handleCloseModal = () => {
    setOpenDeleteModalExp(false);
  };

  const loadEvaluationData = useCallback(async () => {
    if (baseId && jobId) {
      setLoading(true); // Start loader
      try {
        const res = await dispatch(
          fetchEvaluationData({
            baseId: Number(baseId),
            JobId: jobId,
            applicationId: applicationId,
          }),
        ).unwrap();

        setElements(res?.data || []);
      } catch (error) {
        console.error("Error fetching evaluation details:", error);
        setError("Failed to load evaluation data");
      } finally {
        setLoading(false); // Stop loader
      }
    }
  }, [baseId, jobId, dispatch]);

  useEffect(() => {
    loadEvaluationData();
  }, [loadEvaluationData]);

  const handleMeeting = () => {
    setOpenMeeting(true);
  };

  const handleReviewSuccess = (
    currentUserId: string | number,
    reviewId: string | number,
  ) => {
    // setOpenMeeting(false);
    // setEditMeeting(false);
    // Refresh the list immediately
    loadEvaluationData();

    // if (userId == currentUserId) {
    //   navigate(
    //     `/${baseId}/job/${jobId}/app/${applicationId}/appreview/${reviewId}`,
    //   );
    // }
  };

  const formatDate = (createdAt) => {
    return format(new Date(createdAt), "dd MMM yyyy"); // e.g., "28 May 2025"
  };

  const handleEvaluationDetails = (meeting: any) => {
    navigate(
      `/${baseId}/job/${jobId}/app/${applicationId}/appevaluation/${meeting}`,
    );
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] lg:text-[18px] font-medium leading-5 text-[#1b1b1b] tracking-[-0.01rem] capitalize">
          Evaluation
        </h2>
        <button
          onClick={handleMeeting}
          className="group inline-flex shrink-0 select-none items-center justify-end text-[.8125rem] leading-6 transition-colors 
          duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none 
          gap-0 px-[14px] font-medium  py-1 rounded-md text-white bg-indigo-600 h-[32px] hover:bg-indigo-500 focus-visible:outline-indigo-600"
          type="button"
        >
          <div className="flex items-center gap-[6px]">Create Evaluation</div>
        </button>
      </div>
      <div className="bg-[#e0e0e0] w-full h-[1px] mt-8 mb-6"></div>

      {/* <div className="flex items-center gap-x-3 mt-6 mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-devices-check w-[1rem] h-[1rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M13 15.5v-6.5a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v4" />
          <path d="M18 8v-3a1 1 0 0 0 -1 -1h-13a1 1 0 0 0 -1 1v12a1 1 0 0 0 1 1h7" />
          <path d="M16 9h2" />
          <path d="M15 19l2 2l4 -4" />
        </svg>

        <span className="font-medium text-dark-black-200  mr-1 text-[15px] flex items-center gap-[6px] ">
          Evaluation
        </span>
      </div> */}

      {/* Loading state */}
      {loading && (
        <div
          className="flex items-center justify-center relative z-50"
          style={{ height: "calc(50vh - 191px)" }}
        >
          <div className="simple-spinner-loader">
            <span></span>
          </div>
        </div>
      )}
      {/* Error state */}
      {error && (
        <div className="text-center py-4 text-red-500">Error: {error}</div>
      )}

      {/* No meetings state */}
      {!loading && !error && (!elements || elements.length === 0) && (
        <div className="w-full mt-5 flex h-[300px] justify-center items-center flex-col gap-y-3">
          <ExclamationIcon />
          <p className="text-[#0000007a] text-[13px]">No results found</p>
        </div>
      )}

      {/* Meeting list */}
      {!loading && !error && elements && (
        <div className="space-y-4">
          {elements.map((meeting, index) => {
            let assignedUser = Number(meeting.assigned_to);
            const senderUser =
              baseMembers.find((member) => member.user_id === assignedUser) ||
              {};
            const senderImg = senderUser.profile_pic || null;
            const senderName = senderUser.name || null;
            const formattedDate = formatDate(meeting.updated_at);

            const dropdownGroups = [
              {
                options: [
                  {
                    label: `Edit`,
                    className:
                      "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                    onClick: () => handleEditClick(meeting, index),
                  },
                  {
                    label: `Delete`,
                    className:
                      "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                    onClick: () => handleDeleteClick(meeting, index),
                  },
                ],
              },
            ];

            return (
              <div
                key={meeting.assigned_to}
                className="rounded-md  cursor-pointer ring-1 ring-gray-200 feedback-delete-hover"
              >
                <div className="flex items-start px-4 py-4">
                  <div
                    className="flex flex-row w-[82%] gap-x-4 items-start"
                    onClick={
                      // assignedUser === userId
                      //   ?
                      () => handleEvaluationDetails(meeting.assigned_to)
                      // : undefined
                    }
                  >
                    <div className="[95%] flex flex-col gap-y-1">
                      {/* <h2 className="text-sm text-[#374151] font-medium leading-5 mt-[-2px] capitalize flex gap-2"> */}
                      {/* {meeting.review_name} */}
                      {/* Qestion Assigned */}
                      <div className="flex items-center gap-x-2">
                        <div className="">
                          {senderImg ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/${senderImg}`}
                              alt={`${meeting?.review_name || "Profile"}`}
                              className="h-5 w-5 flex-none rounded-full ring-1 ring-gray-900/10"
                            />
                          ) : assignedUser === 0 ? (
                            <AiIcon />
                          ) : (
                          
                            <div className="h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-600">
                              {senderName?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        {senderName && (
                          <h2 className=" text-[#374151] font-medium leading-5 capitalize mt-0">
                            {senderName}
                          </h2>
                        )}
                        {assignedUser === 0 && (
                          <h2 className=" text-[#374151] font-medium leading-5 capitalize mt-0">
                            AI
                          </h2>
                        )}
                      </div>
                      {/* </h2> */}

                      <div className="flex items-center gap-x-2">
                        {/* <div className="">
                          {senderImg ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/${senderImg}`}
                              alt={`${meeting?.review_name || "Profile"}`}
                              className="h-4 w-4 flex-none rounded-full ring-1 ring-gray-900/10"
                            />
                          ) : (
                            <div
                              style={{
                                backgroundColor: getColorForLabel(
                                  meeting?.review_name?.charAt(0) || "",
                                ),
                              }}
                              className="w-4 h-4 rounded-full flex items-center justify-center capitalize"
                            >
                              <span className="font-medium text-white mt-[0px] text-[10px]">
                                {meeting?.review_name?.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        {senderName && (
                          <div className="text-[12px] text-[#374151] font-medium leading-5 capitalize mt-0">
                            {senderName}
                          </div>
                        )} */}
                        {/* {meeting.date && (
                          <div className="mx-[0px] w-[1px] h-[12px] bg-gray-400 block"></div>
                        )} */}
                        {meeting.updated_at && (
                          <span className="text-xs text-gray-500">
                            {formattedDate}
                          </span>
                        )}
                      </div>
                      {/* {
                        dateInfo.fullDate &&
                        <div className='text-xs leading-4 text-gray-500 mt-[2px]'>
                          {dateInfo.fullDate}
                          {
                            dateInfo.time &&
                            <span className='ml-1'>
                              -    {dateInfo.time}
                            </span>
                          }
                        </div>
                      } */}
                      {/* <div className='text-xs leading-4 text-gray-500 mt-1'>Location: {meeting.location || "N/A"}{meeting.location_details ? ` - ${meeting.location_details}` : ""}</div> */}
                    </div>
                  </div>

                  <div className="w-[18%] flex items-start justify-end">
                    <div className=" table-tooltip flex items-center max-w-fit gap-[6px]">
                      {meeting.status === "done" ? (
                        <span
                          className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 capitalize "
                          // style={{ borderColor: bgColor, borderWidth: 1, borderStyle: 'solid' }}
                        >
                          {meeting.status}
                        </span>
                      ) : meeting.status === "pending" ? (
                        <span
                          className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize"
                          // style={{ borderColor: bgColor, borderWidth: 1, borderStyle: 'solid' }}
                        >
                          {meeting.status}
                        </span>
                      ) : meeting.status === "close" ? (
                        <span
                          className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10 capitalize"
                          // style={{ borderColor: bgColor, borderWidth: 1, borderStyle: 'solid' }}
                        >
                          {meeting.status}
                        </span>
                      ) : meeting.status === "cancelled" ? (
                        <span
                          className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10 capitalize"
                          // style={{ borderColor: bgColor, borderWidth: 1, borderStyle: 'solid' }}
                        >
                          {meeting.status}
                        </span>
                      ) : meeting.status === "scheduled" ? (
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 capitalize">
                          {meeting.status}
                          {/* completed-payment */}
                        </span>
                      ) : null}
                    </div>
                    {meeting.review_by === userId && (
                      // className="[&>div]:shrink feedback-delete-btn-hover"
                      <div>
                        <CustomMenuDropdown
                          sections={dropdownGroups}
                          icon={
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="12" cy="12" r="1"></circle>
                              <circle cx="12" cy="5" r="1"></circle>
                              <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                          }
                          menuItemsClassName="left-auto"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {meeting.comment && (
                  <div className="px-0">
                    <div className="bg-[#e0e0e0] w-full h-[1px] mb-0"></div>
                    <div className="text-sm text-[#374151] font-normal leading-5 px-4 py-4  whitespace-pre-wrap">
                      {meeting.comment}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateEvaluation
        opneMeeting={openMeeting}
        setOpenMeeting={setOpenMeeting}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
}

export default ApplicationEvaluationSection;
